/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { INotesConfig } from 'app/core/models/essette-interfaces';
import { NoteService } from 'app/core/services/note-service';
import { Toaster } from 'app/shared/lib/toaster';
import { bindable } from 'aurelia-typed-observable-plugin';
import { forceNull } from 'app/shared/lib/utilities';
import { getReference } from 'app/shared/models/essette-types';
import { apiCall } from 'app/shared/lib/essette-api';
import { I18N } from 'aurelia-i18n';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { NoteCategoryReloadEvent } from 'app/core/lib/events';
import * as Core from 'app/core/models/essette-api';

@autoinject
export class SaveNoteSubCategory extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView })
    protected noteSubCategory: Core.NoteSubCategory = {};

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    protected notesConfig?: INotesConfig;

    protected noteCategoriesReload: Subscription | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected noteFlags: Core.NoteFlag[] = [];
    protected selectedNoteFlags: Core.NoteFlag[] = [];
    protected noteCategories: Core.NoteCategory[] = [];
    protected selectedNoteCategory: Core.NoteCategory | null = null;

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly toaster: Toaster,
        private readonly service: NoteService,
        private readonly i18n: I18N,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        // verify that component is properly configured
        if(!this.notesConfig) throw new Error('Bindable property [notes-config] was not set');
        if(!this.notesConfig?.serviceRootUrl) throw new Error('[serviceRootUrl] was not configured on bindable property [notes-config]');
        this.service.configure(this.notesConfig);

        // listen for updates from the Note Categories tab
        this.noteCategoriesReload = this.events.subscribe(NoteCategoryReloadEvent, async (e: NoteCategoryReloadEvent) => {
            await this.loadNoteCategories();
        });
    }

    protected async attached(): Promise<void> {
        await this.loadNoteCategories();
    }

    ////****************** Events ***************************************************////
    protected noteSubCategoryChanged(): void {
        this.selectedNoteFlags = this.noteSubCategory?.flags ?? [];
        this.selectedNoteCategory = this.noteSubCategory.category ?? null;
    }

    ////****************** Functions ************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public reset(): void {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return this.saveNoteSubCategory();
    }

    protected constructSaveObject(): Core.NoteCategory {
        const saveNoteSubCategory: Core.NoteSubCategory = {
            id: this.noteSubCategory.id,
            category: getReference(this.selectedNoteCategory),
            description: this.noteSubCategory.description,
            code: forceNull(this.noteSubCategory.code),
            flags: []
        }

        this.selectedNoteFlags.forEach(noteFlag => {
            const noteFlagReference = getReference(noteFlag);
            if(noteFlagReference) saveNoteSubCategory.flags!.push(noteFlagReference);
        });

        return saveNoteSubCategory;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadNoteCategories(): Promise<void> {
        const options: StandardLoadOptions = { all: true };

        try {
            this.state.beginLoad();
            const response = await this.service.loadCategories(options);
            this.noteCategories = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveNoteSubCategory(): Promise<boolean> {
        let isSuccessful = false;
        let duplicateCodeError = false;
        try {
            this.state.beginSave();
            const response = await this.service.saveNoteSubCategory(this.constructSaveObject());
            isSuccessful = response.isSuccessful;
            return isSuccessful;
        } catch(error) {
            if(error.status?.errors) duplicateCodeError = error.status.errors.some((err: any) => err.code === 'property.constraintViolated' && err.property === 'code');
            throw error;
        } finally {
            if(duplicateCodeError) {
                if(this.noteSubCategory.id) {
                    this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.update_error', { what: this.i18n.tr('core:note.category.subCategory.code.$'), item: this.i18n.tr('core:note.category.subCategory.$long') })).show();
                } else {
                    this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.create_error', { what: this.i18n.tr('core:note.category.subCategory.code.$'), item: this.i18n.tr('core:note.category.subCategory.$long') })).show();
                }
                this.toaster.error().message(this.i18n.tr('ui:messages.warnings.inUse', { what: this.i18n.tr('core:note.category.subCategory.code.$') })).show();
            } else {
                if(this.noteSubCategory.id) {
                    this.toaster.standard.updated('core:note.category.subCategory.$long', isSuccessful);
                } else {
                    this.toaster.standard.created('core:note.category.subCategory.$long', isSuccessful);
                }
            }
            this.state.endSave();
        }
    }
}
